import _ from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */

/** API */
import MaterialsApi from 'api/warehouses/material.api'
import { routeName } from 'routes/routes-name'

/** UTILS */
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import ProductTypesApi from 'api/setting/default-products/product-type.api'
import HeaderCard from 'component/CardCustom/HeaderCard'
import TableRowExport from 'component/Pdf/TableRowExport'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import { notiError } from 'component/notifications/notifications'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'
import { numberFormat, checkAddons } from 'utils/app.utils'
import { PACKAGE_ADDONS } from 'constants/package'
import { dateTimeToView } from 'utils/date.utils'
import { exportMaterialsToExcel } from './exportMaterialsToExcel'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}


export default function Materials() {
  const { t } = useTranslation()
  const componentRef = useRef<any>(null)
  const [materials, setMaterials] = useState([])
  const [exportData, setExportData] = useState([])

  const [materialTypes, setMaterialTypes] = useState([]);
  const [materialType, setMaterialType]: any = useState({});
  const [metTypeLoading, setMetTypeLoading] = useState(false);

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('productCode')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')
  const [pageTypeProduct, setPageTypeProduct] = useState(1)



  const loadData = async () => {
    try {
      let condition = {}
      condition = {
        group: 'MATERIAL',
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === "ALL" ? '' : status,
        itemTypeId: materialType?.productTypeId === "ALL" ? '' : materialType?.productTypeId
      }

      const resp = await MaterialsApi.findAll(condition)
      const respExport = await MaterialsApi.findAll({ ...condition, export: 1 })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setMaterials(resp.data)
        setExportData(respExport.data)
      } else {
        setMaterials([])
        setExportData([])
      }
    } catch (error) {
      notiError(t('ERROR'))
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])



  const loadProductTypes = (medSearch?: string) => {
    setMetTypeLoading(true)
    const condition: any = { group: 'MATERIAL', pageLimit: 50, search: medSearch || '' }
    ProductTypesApi.findAll(condition)
      .then(({ data }) => setMaterialTypes(data))
      .catch((e) => { return })
      .finally(() => setMetTypeLoading(false))
  }

  useEffect(() => {
    if (materialTypes.length > 0 && _.isEmpty(materialType)) {
      setMaterialType({ productTypeId: 'ALL', productTypeName: t('ALL') });
    }
  }, [materialType]);

  const handleInputSearch = useCallback(
    _.debounce((medSearch?: string) => {
      loadProductTypes(medSearch)
      setPage(1)
    }, 1000), []);

  useEffect(() => {
    loadProductTypes()
  }, []);

  const onEdit = (data: any) => {
    window.location.href = `${routeName.material}/${data.productId}`
  }

  const onCreate = () => {
    window.location.href = routeName.material + '/create'
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const onActive = () => {
    if (id) {
      MaterialsApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('MATERIAL.MSG.UPDATE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('MATERIAL.MSG.UN_SUCCESS'))
        }
      })
    }
  }

  const onDelete = () => {
    if (id) {
      MaterialsApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('MATERIAL.MSG.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('MATERIAL.MSG.UN_SUCCESS'))
        }
      })
    }
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: t('TABLE.NO'), width: '80px' },
    { id: 'productTypeId', disablePadding: false, label: t('MATERIAL.TABLE.CELLS_MED.MATERIAL_TYPE'), width: '200px' },
    { id: 'productCode', disablePadding: false, label: t('MATERIAL.TABLE.CELLS_MED.MATERIAL_CODE'), width: '150px' },
    { id: 'productName', disablePadding: false, label: t('MATERIAL.TABLE.CELLS_MED.MATERIAL_NAME') },
    { id: 'price', disablePadding: false, label: t('MATERIAL.TABLE.CELLS_MED.PRICE'), width: '150px' },
    { id: 'status', disablePadding: false, label: t('MATERIAL.TABLE.CELLS_MED.STATUS'), width: '150px', align: 'center' },
    { id: 'updatedBy', disablePadding: false, label: t('MATERIAL.TABLE.CELLS_MED.UPDATED_BY'), width: '175px', align: 'center' },
    { id: 'action', disablePadding: false, label: 'Action', width: '80px' }
  ]

  const renderData = (objData: any, no: number, valueExportData: boolean) => {
    const num = valueExportData === true ? no + 1 : page * pageLimit - pageLimit + no + 1
    const { productId, productType, productCode, productName, salePrice, updatedBy, updatedAt, status: medStatus } = objData

    const colUpdateExport = {
      option: 'TEXT', align: 'left', label: `${updatedBy} ${dateTimeToView(updatedAt)}`
    }
    const renderColUpdate = valueExportData ? colUpdateExport : { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } }

    const statusBtnActive = medStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = medStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: productId,
      id: productId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: num },
        { option: 'TEXT', align: 'left', label: productType.productTypeName },
        { option: 'TEXT', align: 'left', label: productCode },
        { option: 'TEXT', align: 'left', label: productName },
        {
          option: 'COMPONENT',
          align: 'right',
          component: (
            <div style={{ textAlign: 'right', lineHeight: '1.3' }}>
              <div style={{ whiteSpace: 'nowrap' }}>{numberFormat(salePrice)}</div>
              {checkAddons(PACKAGE_ADDONS.ADDON_PRICE_RANGE) && objData?.isPriceRange === '1' && objData?.maxSalePrice && (
                <div style={{ fontSize: '11px', color: '#808080', whiteSpace: 'nowrap' }}>({numberFormat(objData.minSalePrice || 0)} - {numberFormat(objData.maxSalePrice)})</div>
              )}
            </div>
          )
        },
        { option: 'STATUS', align: 'center', label: medStatus },
        { ...renderColUpdate },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <>
        {valueExportData === true && <TableRowExport {...objRenderData} /> ||
          <TableRowCommon
            {...objRenderData}
            onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
            oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
            onedit={() => onEdit(objRenderData.obj)}
            ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
          />
        }
      </>
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.productName)
  }

  const filter = [
    { label: `${t('INPUT.SEARCH')}: ${search || '-'}` },
    { label: `${t('MATERIAL.TEXT.TYPE')}: ${!_.isEmpty(materialType) ? materialType?.productTypeName : 'ทั้งหมด'}` }
  ]

  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`MATERIAL.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`MATERIAL.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { productName: name, status: t(`MATERIAL.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('MATERIAL.HEADER.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('MATERIAL.TEXT.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('MATERIAL.TEXT.TYPE')}
              value={materialType?.productTypeId || 'ALL'}
              onChange={(event) => {
                const selected = materialTypes.find((item: any) => item.productTypeId === event.target.value)
                setMaterialType(selected || {})
              }}
              options={[
                { value: 'ALL', name: t('ALL') },
                ...materialTypes.map((item: any) => ({
                  value: item.productTypeId,
                  name: item.productTypeName
                }))
              ]}
              loading={metTypeLoading}
              width="100%"
              onOpen={() => loadProductTypes()}
            // onLoadMore={() => {
            //   setPageTypeProduct(pageTypeProduct + 1)
            //   loadProductTypes()
            // }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>


          <Grid item xs={12} sm={6} md={3} xl={2}>
            <MainButton
              title={t('INPUT.SEARCH')}
              type='primary'
              variant='contained'
              style={{ width: '120px' }}
              onClick={() => {
                setPage(1)
                loadData()
              }} />
          </Grid>
          <Grid item xs={12} sm={12} md={12} xl={4}>
            <Box display={'flex'} justifyContent={'flex-end'}>
              <Box display="flex" flexDirection="row" alignItems="center" gap="16px">
                <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                  if (e === 'Excel') {
                    const report: any = {
                      title: t('MATERIAL.HEADER.TITLE'),
                      statusFilter: `${filter[1].label} ${status === 'ALL' ? `${t('STATUS.LABEL')}: ${t('STATUS.ALL')}` : `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}`}`,
                      rows: exportData.map((item: any) => ({
                        materialTypeName: item?.productType?.productTypeName || '-',
                        materialCode: item?.productCode || '-',
                        materialName: item?.productName || '-',
                        salePrice: item?.salePrice || '0.00',
                        status: t(`STATUS.${item?.status}`),
                        updatedBy: item?.updatedBy,
                        updatedAt: dateTimeToView(item?.updatedAt)
                      })),
                      t: t
                    }
                    exportMaterialsToExcel(report)
                  } else if (e === 'PDF') {
                    const style = document.createElement("style");
                    style.innerHTML = `
                      @media print {
                        @page { size: landscape }
                      }
                    `;
                    document.head.appendChild(style);
                    window.print();
                    document.head.removeChild(style);

                  }
                }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                <MainButton title={t('MATERIAL.BUTTON.ADD_MED')} disabled={permissions.isCreate.disabled} type='primary' variant='contained' onClick={() => onCreate()} startIcon={<AddCircleIcon />} />
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={materials.map((val, i) => {
              return renderData(val, i, false)
            })}
          />
        </Box>
      </Box>

      <Box
        id="print-table-container"
        ref={componentRef}
      >
        <Typography fontSize="14px" fontWeight={400}>{t('MATERIAL.HEADER.TITLE')}</Typography>
        <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
          <Typography fontSize="10px" fontWeight={400}>{`${t('MATERIAL.TEXT.TYPE')}: ${!_.isEmpty(materialType) ? materialType?.productTypeName : 'ทั้งหมด'}`}</Typography>
          <Typography fontSize="10px" fontWeight={400}>{`${t('STATUS.LABEL')}: ${!_.isEmpty(status) ? t(`STATUS.${status}`) : '-'}`}</Typography>
        </Box>
        <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
          <thead>
            <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
              <th style={{ borderBottom: '1px solid black' }}>{t('#')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('MATERIAL.TABLE.CELLS_MED.MATERIAL_TYPE')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('MATERIAL.TABLE.CELLS_MED.MATERIAL_CODE')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('MATERIAL.TABLE.CELLS_MED.MATERIAL_NAME')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('MATERIAL.TABLE.CELLS_MED.PRICE')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('MATERIAL.TABLE.CELLS_MED.STATUS')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('MATERIAL.TABLE.CELLS_MED.UPDATED_BY')}</th>
            </tr>
          </thead>
          <tbody>
            {exportData?.length === 0 &&
              <tr>
                <td colSpan={7} style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100%' }}>{t('NOT_FOUND')}</td>
              </tr>
            }
            {exportData.map((data: any, index: number) => (
              <tr key={index}>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{index + 1}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '100px' }}>{data?.productType.productTypeName || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '100px' }}>{data?.productCode || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{data?.productName || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'top', width: '100px', lineHeight: '1.3' }}>
                  <div style={{ whiteSpace: 'nowrap' }}>{numberFormat(data?.salePrice)}</div>
                  {checkAddons(PACKAGE_ADDONS.ADDON_PRICE_RANGE) && data?.isPriceRange === '1' && data?.maxSalePrice && (
                    <div style={{ fontSize: '8px', color: '#808080', whiteSpace: 'nowrap' }}>({numberFormat(data.minSalePrice || 0)} - {numberFormat(data.maxSalePrice)})</div>
                  )}
                </td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '85px' }}>{t(`STATUS.${data?.status}`) || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <span>{data?.updatedBy || '-'}</span>
                  <span>{dateTimeToView(data?.updatedAt) || '-'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  )
}
